import React, { useState, useCallback, useEffect, useRef } from 'react'
import {
  LiveKitRoom,
  GridLayout,
  ParticipantTile,
  RoomAudioRenderer,
  TrackToggle,
  DisconnectButton,
  useTracks,
  useRoomContext,
} from '@livekit/components-react'
import '@livekit/components-styles'
import { Room, Track } from 'livekit-client'
import { Box, Typography, IconButton, Tooltip } from '@mui/material'
import {
  MenuBook as MenuBookIcon,
  MenuBookOutlined as MenuBookOutlinedIcon,
} from '@mui/icons-material'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { PrescriptionData } from '@/shared/types/yakurekiPrescription'
import { useVideoCallRecording } from '../hooks/useVideoCallRecording'
import { VideoCallGuidancePanel } from './VideoCallGuidancePanel'

// 通話画面 dark chrome の hex は Notion canonical token に対応物が無いため source-of-truth として
// 本 file 内に集約. dark theme は本 PJ scope 外なので canonical 化は将来別タスクで.
const DARK_CHROME = {
  surface: '#0F0F0F', // ヘッダー / control bar 背景 (旧 #1a1a2e から純黒寄りに)
  body: '#000000', // ビデオ枠の背景
  text: '#E8E8E6', // 本文 (純白を避け warm gray)
  textMuted: '#9B9B9B', // 二次テキスト
  divider: '#2A2A2A', // sidepanel 境界
  accentRecord: colorTokens.state.recordingIcon, // #D44C47
  accentBookActive: '#7DB6E8', // 指導支援 active 色 (旧 #90caf9 を warm 寄りに)
} as const

interface VideoCallRoomProps {
  livekitUrl: string
  livekitToken: string
  yakurekiId?: string
  currentPrescription?: PrescriptionData
  previousPrescription?: PrescriptionData
  onDisconnected: (audioBlob: Blob | null) => void
}

export const VideoCallRoom: React.FC<VideoCallRoomProps> = ({
  livekitUrl,
  livekitToken,
  yakurekiId,
  currentPrescription,
  previousPrescription,
  onDisconnected,
}) => {
  const [room, setRoom] = useState<Room>()
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [showGuidance, setShowGuidance] = useState(false)

  const { isRecording, startRecording, stopRecording, audioBlob } =
    useVideoCallRecording(room)

  // 通話タイマー
  useEffect(() => {
    if (!isRecording) return
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1)
    }, 1000)
    return () => clearInterval(interval)
  }, [isRecording])

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0')
    const s = (seconds % 60).toString().padStart(2, '0')
    return `${m}:${s}`
  }

  const handleRoomReady = useCallback(
    (connectedRoom: Room) => {
      setRoom(connectedRoom)
      startRecording()
    },
    [startRecording]
  )

  const handleDisconnected = useCallback(() => {
    stopRecording()
  }, [stopRecording])

  const onDisconnectedRef = useRef(onDisconnected)
  useEffect(() => {
    onDisconnectedRef.current = onDisconnected
  }, [onDisconnected])

  // audioBlob が設定されたら（録音停止後）親に通知
  // LiveKit 内部クリーンアップ完了を待ってからアンマウントするため遅延
  useEffect(() => {
    if (audioBlob) {
      const timer = setTimeout(() => {
        onDisconnectedRef.current(audioBlob)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [audioBlob])

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: DARK_CHROME.body,
      }}
    >
      <>
        {/* ヘッダー — Notion 流 dense: body2 タイトル + caption 経過時間 + 録音 dot inline */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1,
            bgcolor: DARK_CHROME.surface,
            color: DARK_CHROME.text,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            通話中
          </Typography>
          <Typography variant="caption" sx={{ color: DARK_CHROME.textMuted }}>
            {formatTime(elapsedSeconds)}
          </Typography>
          {isRecording && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: DARK_CHROME.accentRecord,
                }}
              />
              <Typography
                variant="caption"
                sx={{ color: DARK_CHROME.textMuted }}
              >
                録音中
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {yakurekiId && (
            <Tooltip
              title={showGuidance ? '指導支援を閉じる' : '指導支援を表示'}
            >
              <IconButton
                size="small"
                onClick={() => setShowGuidance((prev) => !prev)}
                sx={{
                  color: showGuidance
                    ? DARK_CHROME.accentBookActive
                    : DARK_CHROME.text,
                }}
              >
                {showGuidance ? (
                  <MenuBookIcon fontSize="small" />
                ) : (
                  <MenuBookOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <LiveKitRoom
              serverUrl={livekitUrl}
              token={livekitToken}
              connect={true}
              onDisconnected={handleDisconnected}
              data-lk-theme="default"
              style={{ height: '100%' }}
            >
              <VideoLayout />
              <RoomAudioRenderer />
              <RoomConnector onRoomReady={handleRoomReady} />
            </LiveKitRoom>
          </Box>

          {/* 指導支援サイドパネル — Notion light surface (= bg.default) で対比 */}
          {showGuidance && yakurekiId && (
            <Box
              sx={{
                width: '40%',
                maxWidth: 500,
                minWidth: 320,
                bgcolor: colorTokens.bg.default,
                overflowY: 'auto',
                borderLeft: `1px solid ${DARK_CHROME.divider}`,
              }}
            >
              <VideoCallGuidancePanel
                yakurekiId={yakurekiId}
                currentPrescription={currentPrescription}
                previousPrescription={previousPrescription}
              />
            </Box>
          )}
        </Box>
      </>
    </Box>
  )
}

/**
 * カスタムビデオレイアウト（日本語ラベル付きコントロールバー）
 */
const VideoLayout: React.FC = () => {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false }
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <GridLayout tracks={tracks} style={{ height: '100%' }}>
          <ParticipantTile />
        </GridLayout>
      </Box>
      {/* コントロールバー — Notion 流 pill button (= dark surface 上で subtle, danger は明示) */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          py: 1.25,
          bgcolor: DARK_CHROME.surface,
        }}
      >
        <TrackToggle
          source={Track.Source.Microphone}
          style={{
            padding: '6px 14px',
            borderRadius: 999,
            border: 'none',
            background: 'transparent',
            color: DARK_CHROME.text,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          マイク
        </TrackToggle>
        <TrackToggle
          source={Track.Source.Camera}
          style={{
            padding: '6px 14px',
            borderRadius: 999,
            border: 'none',
            background: 'transparent',
            color: DARK_CHROME.text,
            cursor: 'pointer',
            fontSize: 13,
          }}
        >
          カメラ
        </TrackToggle>
        <DisconnectButton
          style={{
            padding: '6px 18px',
            borderRadius: 999,
            border: 'none',
            backgroundColor: DARK_CHROME.accentRecord,
            color: '#FFFFFF',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          指導を終える
        </DisconnectButton>
      </Box>
    </Box>
  )
}

/**
 * LiveKitRoom 内部で Room インスタンスを取得するためのヘルパーコンポーネント
 */
const RoomConnector: React.FC<{
  onRoomReady: (room: Room) => void
}> = ({ onRoomReady }) => {
  const room = useRoomContext()

  useEffect(() => {
    if (room) {
      onRoomReady(room)
    }
  }, [room, onRoomReady])

  return null
}
